/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * De routes van de applicatie (sinds golf 4e), de opvolgers van de zes states uit de AngularJS-versie
 * (`app/states/states.js`). Vier url's zijn een contract met de buitenwereld en houden daarom hun vorm
 * (`ontwerp-fase4.md` § 3, bevinding 3):
 *
 *   #!/actie/fromAdmin/<adminKey>   de admin opent een model van een student
 *   #!/code/<projectcode>           een docent deelt een projectcode; die link gaat jaren mee
 *   #!/lesdirect/<secret>           het backend stuurt vanuit material hierheen door (HTTP 303)
 *   #!/ww/<token>                   de wachtwoordlink uit een mail van het backend
 *
 * De hash blijft dus (kernbesluit 2), maar Angular schrijft `#/` waar ng1 `#!/` schreef. De `!` wordt op
 * twee plekken weggehaald: bij het opstarten in `app.config.ts` (`normaliseerHash`, voor de adresbalk)
 * en hier in de `**`-route (voor een hashwijziging in een tab die al openstaat — dan draait geen
 * initializer meer).
 */
import {inject} from '@angular/core';
import {CanActivateFn, Router, Routes} from '@angular/router';
import {SessieService} from '../services/sessie.service';
import {DlBuildComponent} from '../components/schermen/dl-build/dl-build.component';
import {DlLoginComponent} from '../components/schermen/dl-login/dl-login.component';
import {DlWachtwoordlinkComponent} from '../components/schermen/dl-wachtwoordlink/dl-wachtwoordlink.component';

/**
 * Wie al is ingelogd hoort niet op het inlogscherm: die gaat door naar het bouwscherm, met de actie uit
 * de url als queryparams.
 *
 * Een guard en geen doorstuur in het component: zou de `actie`-route een component activeren, dan
 * vervangt de router-outlet het bouwscherm van een tab die al openstaat — met de canvassen erbij. Een
 * redirect uit een guard activeert de route nooit, dus `DlBuildComponent` blijft staan en krijgt de
 * nieuwe queryparams.
 */
export const actieGuard: CanActivateFn = async route => {
    const router = inject(Router);
    if (!await inject(SessieService).isIngelogd()) {
        return true; //het inlogscherm mag komen
    }
    const actie = route.paramMap.get('openAction');
    const arg = route.paramMap.get('openArg');
    return router.createUrlTree(['/build'], {queryParams: actie ? {actie, arg} : {}});
};

/**
 * Het bouwscherm is alleen voor wie is ingelogd. Zo niet, dan naar het inlogscherm, met de actie mee —
 * die hoort na het inloggen alsnog uitgevoerd te worden.
 */
export const ingelogdGuard: CanActivateFn = async route => {
    const router = inject(Router);
    if (await inject(SessieService).isIngelogd()) {
        return true;
    }
    const actie = route.queryParamMap.get('actie');
    const arg = route.queryParamMap.get('arg');
    return actie ? router.createUrlTree(['/actie', actie, arg ?? '']) : router.createUrlTree(['/']);
};

/**
 * De lesdirect-sleutel uit material: inloggen en door naar het bouwscherm. Een guard en geen scherm — er
 * is niets te tonen, en een mislukte poging hoort net als in AngularJS op het inlogscherm te eindigen.
 */
export const lesdirectGuard: CanActivateFn = async route => {
    const router = inject(Router);
    const sleutel = route.paramMap.get('lesdirectkey');
    if (!sleutel) {
        return router.parseUrl('/');
    }
    try {
        await inject(SessieService).lesdirectLogin(sleutel);
        return router.parseUrl('/build');
    } catch (_fout) {
        return router.parseUrl('/'); //mislukt is mislukt
    }
};

/**
 * Alles wat geen route is. Een url die met `#!/` binnenkwam, wordt hier `#/`: dat is de vorm van de vier
 * contracten, en bij een hashwijziging in een draaiende tab is dit de enige plek waar dat nog kan. De
 * rest gaat naar het inlogscherm, zoals `$urlRouterProvider.otherwise('/')` in AngularJS.
 */
export const restGuard: CanActivateFn = (_route, stand) => {
    const router = inject(Router);
    const zonderUitroepteken = stand.url.replace(/^\/!\//, '/');
    return router.parseUrl(zonderUitroepteken === stand.url ? '/' : zonderUitroepteken);
};

export const routes: Routes = [
    {path: '', canActivate: [actieGuard], component: DlLoginComponent},
    {path: 'actie/:openAction/:openArg', canActivate: [actieGuard], component: DlLoginComponent},
    {path: 'code/:projectcode', component: DlLoginComponent},
    {path: 'lesdirect/:lesdirectkey', canActivate: [lesdirectGuard], component: DlLoginComponent},
    {path: 'ww/:wwlink', component: DlWachtwoordlinkComponent},
    {path: 'build', canActivate: [ingelogdGuard], component: DlBuildComponent},
    {path: '**', canActivate: [restGuard], component: DlLoginComponent}
];
